import React, { useState, useCallback, useRef, useEffect } from "react";
import classNames from "classnames";
import { IconSearch, IconArrowHead } from "../../../components";
import { useCategories } from "../../../hooks/useCategories";

import css from "./productSearch.module.css";

// Custom debounce hook
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};

const ProductSearch = ({ searchParams, onSearchUpdate }) => {
  const [searchQuery, setSearchQuery] = useState(searchParams.search || "");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.category || "");
  
  // Use the categories hook
  const { getCategoriesWithAllOption, isLoading: isLoadingCategories } = useCategories();
  const categories = getCategoriesWithAllOption();
  
  // Initialize selectedSort properly considering both sortBy and sortOrder
  const getInitialSortValue = () => {
    let sortValue = searchParams.sortBy || "created";
    const sortOrder = searchParams.sortOrder || "desc";
    
    if (sortValue === "created") {
      sortValue = sortOrder === "desc" ? "created-desc" : "created";
    } else if (sortValue === "price") {
      sortValue = sortOrder === "desc" ? "price-desc" : "price";
    }
    
    return sortValue;
  };
  
  const [selectedSort, setSelectedSort] = useState(getInitialSortValue());
  const [priceRange, setPriceRange] = useState({ 
    min: searchParams.minPrice || "", 
    max: searchParams.maxPrice || "" 
  });

  const filterRef = useRef(null);
  const sortRef = useRef(null);

  // Update local state when searchParams change
  useEffect(() => {
    setSearchQuery(searchParams.search || "");
    setSelectedCategory(searchParams.category || "");
    
    // Reconstruct the full sort value from sortBy and sortOrder
    let sortValue = searchParams.sortBy || "created";
    const sortOrder = searchParams.sortOrder || "desc";
    
    if (sortValue === "created") {
      sortValue = sortOrder === "desc" ? "created-desc" : "created";
    } else if (sortValue === "price") {
      sortValue = sortOrder === "desc" ? "price-desc" : "price";
    }
    
    setSelectedSort(sortValue);
    
    setPriceRange({
      min: searchParams.minPrice || "",
      max: searchParams.maxPrice || ""
    });
  }, [searchParams]);

  // Debounced search update
  const debouncedSearchUpdate = useDebounce((query) => {
    updateSearchParams({ search: query });
  }, 500);

  // Debounced price update
  const debouncedPriceUpdate = useDebounce((minPrice, maxPrice) => {
    updateSearchParams({ minPrice, maxPrice });
  }, 500);

  // Sort options mapping
  const sortOptions = [
    { value: "price", label: "Price: Low to High" },
    { value: "price-desc", label: "Price: High to Low" },
    { value: "created-desc", label: "Newest First" },
    { value: "created", label: "Oldest First" },
  ];

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Keyboard navigation for dropdowns
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsFilterOpen(false);
        setIsSortOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const updateSearchParams = useCallback((newParams) => {
    // Determine sort field and order from selectedSort
    let sortBy = selectedSort;
    let sortOrder = 'desc'; // Default to desc for newest first
    
    if (selectedSort.includes('-desc')) {
      sortBy = selectedSort.replace('-desc', '');
      sortOrder = 'desc';
    } else if (selectedSort === 'created') {
      sortBy = 'created';
      sortOrder = 'asc'; // Oldest first
    } else if (selectedSort === 'price') {
      sortBy = 'price';
      sortOrder = 'asc'; // Low to High
    }
    
    const updatedParams = {
      search: searchQuery,
      category: selectedCategory,
      minPrice: priceRange.min,
      maxPrice: priceRange.max,
      sortBy: sortBy,
      sortOrder: sortOrder,
      ...newParams
    };
    
    if (newParams.sortBy) {
      if (newParams.sortBy.includes('-desc')) {
        updatedParams.sortBy = newParams.sortBy.replace('-desc', '');
        if (updatedParams.sortBy === 'price') {
          updatedParams.sortOrder = 'desc'; 
        } else if (updatedParams.sortBy === 'created') {
          updatedParams.sortOrder = 'desc'; 
        }
      } else {
        updatedParams.sortBy = newParams.sortBy;
        if (newParams.sortBy === 'price') {
          updatedParams.sortOrder = 'asc'; 
        } else if (newParams.sortBy === 'created') {
          updatedParams.sortOrder = 'asc'; 
        }
      }
    } else {
      const hasFilters = newParams.category || newParams.minPrice || newParams.maxPrice || newParams.search;
      if (hasFilters && !newParams.sortBy) {
        updatedParams.sortBy = 'created';
        updatedParams.sortOrder = 'desc';
      }
    }
    
    onSearchUpdate(updatedParams);
  }, [searchQuery, selectedCategory, priceRange, selectedSort, onSearchUpdate]);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    updateSearchParams({ search: searchQuery });
  }, [searchQuery, updateSearchParams]);

  const handleFilterToggle = useCallback(() => {
    setIsFilterOpen(prev => !prev);
    setIsSortOpen(false);
  }, []);

  const handleSortToggle = useCallback(() => {
    setIsSortOpen(prev => !prev);
    setIsFilterOpen(false);
  }, []);

  const handleCategoryChange = useCallback((e) => {
    const newCategory = e.target.value;
    setSelectedCategory(newCategory);
    updateSearchParams({ category: newCategory });
    setIsFilterOpen(false);
  }, [updateSearchParams]);

  const handleSortChange = useCallback((value) => {
    setSelectedSort(value);
    updateSearchParams({ sortBy: value });
    setIsSortOpen(false);
  }, [updateSearchParams]);

  const handlePriceChange = useCallback((field, value) => {
    const newPriceRange = {
      ...priceRange,
      [field]: value,
    };
    setPriceRange(newPriceRange);
    debouncedPriceUpdate(newPriceRange.min, newPriceRange.max);
  }, [priceRange, debouncedPriceUpdate]);

  const handleImmediateSearch = useCallback(() => {
    updateSearchParams({ search: searchQuery });
  }, [searchQuery, updateSearchParams]);

  const clearFilters = useCallback(() => {
    setSelectedCategory("");
    setPriceRange({ min: "", max: "" });
    setSelectedSort("created-desc");
    updateSearchParams({ 
      category: "", 
      minPrice: "", 
      maxPrice: "", 
      sortBy: "created",
      sortOrder: "desc"
    });
    setIsFilterOpen(false);
  }, [updateSearchParams]);

  const hasActiveFilters = selectedCategory || priceRange.min || priceRange.max || selectedSort !== "created-desc" || selectedSort === "created-desc";

  // Compute the correct active sort option
  const getActiveSortOption = useCallback(() => {
    // Simply return the selectedSort value - no need for complex logic
    return selectedSort;
  }, [selectedSort]);

  return (
    <div className={css.root}>
      <div className={css.searchContainer}>
        {/* Main Search Bar */}
        <form className={css.searchForm}>
          <div className={css.searchInputWrapper}>
            <IconSearch className={css.searchIcon} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => {
                const newQuery = e.target.value;
                setSearchQuery(newQuery);
                debouncedSearchUpdate(newQuery);
              }}
              className={css.searchInput}
              aria-label="Search products"
            />
            <button 
              type="button" 
              onClick={handleImmediateSearch}
              className={css.searchButton}
            >
              Search
            </button>
          </div>
        </form>

        {/* Filter and Sort Controls */}
        <div className={css.controlsContainer}>
          {/* Filter Button */}
          <div className={css.filterWrapper} ref={filterRef}>
            <button
              type="button"
              onClick={handleFilterToggle}
              className={classNames(css.filterButton, {
                [css.filterButtonActive]: isFilterOpen || hasActiveFilters,
              })}
              aria-expanded={isFilterOpen}
              aria-haspopup="true"
              aria-label="Open filters"
            >
              <span className={css.filterButtonText}>Filters</span>
              {hasActiveFilters && <span className={css.filterBadge}>•</span>}
              <IconArrowHead
                className={classNames(css.filterArrow, {
                  [css.filterArrowOpen]: isFilterOpen,
                })}
              />
            </button>

            {/* Filter Dropdown */}
            {isFilterOpen && (
              <div 
                className={css.filterDropdown}
                role="dialog"
                aria-label="Filter options"
              >
                <div className={css.filterSection}>
                  <h4 className={css.filterSectionTitle}>Category</h4>
                  <select
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    className={css.filterSelect}
                    aria-label="Select category"
                    disabled={isLoadingCategories}
                  >
                    {isLoadingCategories ? (
                      <option value="">Loading categories...</option>
                    ) : (
                      categories.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className={css.filterSection}>
                  <h4 className={css.filterSectionTitle}>Price Range</h4>
                  <div className={css.priceRangeContainer}>
                    <input
                      type="number"
                      placeholder="Min"
                      value={priceRange.min}
                      onChange={(e) => handlePriceChange("min", e.target.value)}
                      className={css.priceInput}
                      aria-label="Minimum price"
                    />
                    <span className={css.priceSeparator}>-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={priceRange.max}
                      onChange={(e) => handlePriceChange("max", e.target.value)}
                      className={css.priceInput}
                      aria-label="Maximum price"
                    />
                  </div>
                </div>

                <div className={css.filterActions}>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className={css.clearFiltersButton}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sort Button */}
          <div className={css.sortWrapper} ref={sortRef}>
            <button
              type="button"
              onClick={handleSortToggle}
              className={classNames(css.sortButton, {
                [css.sortButtonActive]: isSortOpen,
              })}
              aria-expanded={isSortOpen}
              aria-haspopup="true"
              aria-label="Open sort options"
            >
              <span className={css.sortButtonText}>Sort by</span>
              <IconArrowHead
                className={classNames(css.sortArrow, {
                  [css.sortArrowOpen]: isSortOpen,
                })}
              />
            </button>

            {/* Sort Dropdown */}
            {isSortOpen && (
              <div 
                className={css.sortDropdown}
                role="dialog"
                aria-label="Sort options"
              >
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSortChange(option.value)}
                    className={classNames(css.sortOption, {
                      [css.sortOptionSelected]: getActiveSortOption() === option.value,
                    })}
                    aria-pressed={getActiveSortOption() === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className={css.activeFilters}>
          <span className={css.activeFiltersLabel}>Active filters:</span>
          {selectedCategory && !isLoadingCategories && (
            <span className={css.activeFilter}>
              {categories.find(c => c.value === selectedCategory)?.label || selectedCategory}
              <button
                type="button"
                onClick={() => handleCategoryChange({ target: { value: "" } })}
                className={css.removeFilter}
                aria-label={`Remove ${categories.find(c => c.value === selectedCategory)?.label || selectedCategory} filter`}
              >
                ×
              </button>
            </span>
          )}
          {(priceRange.min || priceRange.max) && (
            <span className={css.activeFilter}>
              Price: ${priceRange.min || "0"} - ${priceRange.max || "∞"}
              <button
                type="button"
                onClick={() => {
                  handlePriceChange("min", "");
                  handlePriceChange("max", "");
                }}
                className={css.removeFilter}
                aria-label="Remove price filter"
              >
                ×
              </button>
            </span>
          )}
          {selectedSort && (
            <span className={css.activeFilter}>
              {sortOptions.find(s => s.value === getActiveSortOption())?.label}
              <button
                type="button"
                onClick={() => handleSortChange("created-desc")}
                className={css.removeFilter}
                aria-label="Remove sort filter"
              >
                ×
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSearch;